/**
 * Projects Hub — swarm-agnostic grouping layer.
 * Create projects per swarm, group swarm task runs into them, and attach
 * "always-used" knowledge (manual upload → RAG grounding) scoped per project.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FolderKanban, Plus, Trash2, Archive, ArchiveRestore, FileText, UploadCloud,
  Loader2, Search, Sparkles, ListChecks, BookOpen, ChevronRight, CheckCircle2, AlertCircle,
  TrendingUp, DollarSign, Layers,
} from "lucide-react";
import { PROJECT_SWARMS, SWARM_LABEL } from "./projectConstants";
import ConnectorImportModal from "./ConnectorImportModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusChip = {
  ready: { color: "#10B981", label: "Ready", Icon: CheckCircle2 },
  processing: { color: "#F59E0B", label: "Processing", Icon: Loader2 },
  queued: { color: "#F59E0B", label: "Queued", Icon: Loader2 },
  parsing: { color: "#F59E0B", label: "Parsing", Icon: Loader2 },
  chunking: { color: "#F59E0B", label: "Chunking", Icon: Loader2 },
  embedding: { color: "#F59E0B", label: "Embedding", Icon: Loader2 },
  failed: { color: "#EF4444", label: "Failed", Icon: AlertCircle },
};

export default function ProjectsHub() {
  const [swarmFilter, setSwarmFilter] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const q = swarmFilter ? `?swarm=${encodeURIComponent(swarmFilter)}` : "";
      const { data } = await axios.get(`${API}/projects${q}`);
      setProjects(data.projects || []);
    } catch (_) { setProjects([]); }
    setLoading(false);
  }, [swarmFilter]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Handle OAuth connector redirect (?connected=google&project=... or ?connect_error=...)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const connected = p.get("connected");
    const err = p.get("connect_error");
    const proj = p.get("project");
    if (connected) toast.success(`${connected === "google" ? "Google Drive" : "SharePoint"} connected`);
    if (err) toast.error(`Connection failed (${err}). Check consent & permissions.`);
    if (proj) openProject(proj);
    if (connected || err || proj) {
      const url = new URL(window.location.href);
      ["connected", "connect_error", "project"].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openProject = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/projects/${id}`);
      setDetail(data);
    } catch (_) { toast.error("Could not open project"); }
  };

  const refreshDetail = () => { if (selected) openProject(selected); };

  return (
    <div data-testid="projects-hub" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--qn-primary-muted)] flex items-center justify-center">
            <FolderKanban className="w-5 h-5 text-[var(--qn-primary)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--qn-foreground)]">Projects</h1>
            <p className="text-sm text-[var(--qn-foreground-muted)]">Group swarm tasks & attach always-used knowledge</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={swarmFilter}
            onChange={(e) => setSwarmFilter(e.target.value)}
            data-testid="projects-swarm-filter"
            className="bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] outline-none"
          >
            <option value="">All swarms</option>
            {PROJECT_SWARMS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-project-btn"
            className="flex items-center gap-2 bg-[var(--qn-primary)] text-white text-sm font-medium rounded-lg px-4 py-2 hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" /> New Project
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* Project list */}
        <div className="space-y-2" data-testid="projects-list">
          {loading && (
            <div className="flex items-center gap-2 text-[var(--qn-foreground-muted)] text-sm py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
            </div>
          )}
          {!loading && projects.length === 0 && (
            <div data-testid="projects-empty" className="rounded-xl border border-dashed border-[var(--qn-border)] p-8 text-center text-sm text-[var(--qn-foreground-muted)]">
              No projects yet. Create one to start grouping swarm tasks.
            </div>
          )}
          {!loading && projects.map((p) => (
            <button
              key={p.id}
              onClick={() => openProject(p.id)}
              data-testid={`project-card-${p.id}`}
              className={`w-full text-left rounded-xl border p-4 transition-all ${
                selected === p.id
                  ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]"
                  : "border-[var(--qn-border)] bg-[var(--qn-card)] hover:border-[var(--qn-primary)]/50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-[var(--qn-foreground)] truncate">{p.name}</span>
                {p.archived && <Archive className="w-3.5 h-3.5 text-[var(--qn-foreground-subtle)] shrink-0" />}
              </div>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-[var(--qn-foreground-muted)]">
                <span className="px-1.5 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">{SWARM_LABEL[p.swarm] || p.swarm}</span>
                <span className="flex items-center gap-1"><ListChecks className="w-3 h-3" /> {p.task_count}</span>
                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {p.knowledge_count}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div>
          {!selected && (
            <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-12 text-center text-[var(--qn-foreground-muted)]" data-testid="projects-detail-empty">
              <ChevronRight className="w-6 h-6 mx-auto mb-2 opacity-40" />
              Select a project to view its tasks and knowledge.
            </div>
          )}
          {selected && !detail && (
            <div className="flex items-center gap-2 text-[var(--qn-foreground-muted)] text-sm py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {selected && detail && (
            <ProjectDetail project={detail} onChanged={() => { refreshDetail(); loadProjects(); }} onDeleted={() => { setSelected(null); setDetail(null); loadProjects(); }} />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateProjectModal
          defaultSwarm={swarmFilter}
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); loadProjects(); openProject(p.id); }}
        />
      )}
    </div>
  );
}

// ── Create modal ────────────────────────────────────────────────────────
function CreateProjectModal({ defaultSwarm, onClose, onCreated }) {
  const [swarm, setSwarm] = useState(defaultSwarm || PROJECT_SWARMS[0].key);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { swarm, name: name.trim(), description });
      toast.success("Project created");
      onCreated(data);
    } catch (e) { toast.error("Could not create project"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="create-project-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-6"
      >
        <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-4">New Project</h2>
        <label className="block text-xs uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-1">Swarm</label>
        <select value={swarm} onChange={(e) => setSwarm(e.target.value)} data-testid="create-project-swarm"
          className="w-full bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] outline-none mb-4">
          {PROJECT_SWARMS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <label className="block text-xs uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-1">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="create-project-name"
          onKeyDown={(e) => { if (e.key === "Enter") create(); }}
          placeholder="e.g. Community Innovation FY26"
          className="w-full bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] outline-none mb-4" />
        <label className="block text-xs uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-1">Description (optional)</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="create-project-description"
          className="w-full bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] outline-none mb-5 resize-none" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-[var(--qn-border)] text-[var(--qn-foreground-muted)]">Cancel</button>
          <button onClick={create} disabled={saving} data-testid="create-project-submit"
            className="px-4 py-2 text-sm rounded-lg bg-[var(--qn-primary)] text-white font-medium flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Project detail ────────────────────────────────────────────────────────
function InsightStat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-xl font-bold ${accent ? "text-[var(--qn-primary)]" : "text-[var(--qn-foreground)]"}`}>{value}</div>
      <div className="text-[11px] text-[var(--qn-foreground-subtle)]">{sub}</div>
    </div>
  );
}

function ProjectDetail({ project, onChanged, onDeleted }) {
  const fileRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const [uploading, setUploading] = useState(false);
  const [gq, setGq] = useState("");
  const [grounding, setGrounding] = useState(null);
  const [gLoading, setGLoading] = useState(false);

  // Poll while any knowledge source is still ingesting so the status pill settles.
  const pendingKey = (project.knowledge_sources || []).map((s) => `${s.id}:${s.status}`).join(",");
  useEffect(() => {
    const pending = (project.knowledge_sources || []).some((s) => !["ready", "failed"].includes(s.status));
    if (!pending) return;
    const t = setTimeout(() => onChangedRef.current(), 2500);
    return () => clearTimeout(t);
  }, [pendingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [insights, setInsights] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/projects/${project.id}/insights`).then(({ data }) => { if (alive) setInsights(data); }).catch(() => {});
    return () => { alive = false; };
  }, [project.id, project.tasks?.length, pendingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
  };

  const archive = async () => {
    try { await axios.patch(`${API}/projects/${project.id}`, { archived: !project.archived }); onChanged(); }
    catch (_) { toast.error("Could not update project"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${project.name}"? Task memberships are removed; underlying runs are preserved.`)) return;
    try { await axios.delete(`${API}/projects/${project.id}`); toast.success("Project deleted"); onDeleted(); }
    catch (_) { toast.error("Could not delete project"); }
  };
  const removeTask = async (taskId) => {
    try { await axios.delete(`${API}/projects/tasks/${taskId}`); onChanged(); }
    catch (_) { toast.error("Could not remove task"); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await axios.post(`${API}/projects/${project.id}/knowledge/upload`, fd);
      toast.success("Uploaded — ingesting into grounding");
      setTimeout(onChanged, 1500);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };
  const deleteSource = async (sourceId) => {
    try { await axios.delete(`${API}/projects/${project.id}/knowledge/${sourceId}`); onChanged(); }
    catch (_) { toast.error("Could not delete source"); }
  };
  const runGrounding = async () => {
    if (!gq.trim()) return;
    setGLoading(true); setGrounding(null);
    try {
      const { data } = await axios.get(`${API}/projects/${project.id}/grounding?query=${encodeURIComponent(gq.trim())}`);
      setGrounding(data);
    } catch (_) { toast.error("Grounding preview failed"); }
    setGLoading(false);
  };

  // Connectors (Google Drive / SharePoint)
  const [connStatus, setConnStatus] = useState(null);
  const [importModal, setImportModal] = useState(null);
  const loadConnStatus = useCallback(() => {
    axios.get(`${API}/connectors/status`).then(({ data }) => setConnStatus(data)).catch(() => {});
  }, []);
  useEffect(() => { loadConnStatus(); }, [loadConnStatus]);

  const connect = async (provider) => {
    try {
      const { data } = await axios.get(`${API}/connectors/${provider}/auth-url?project_id=${project.id}`);
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e?.response?.data?.detail || `${provider} is not configured`); }
  };
  const disconnect = async (provider) => {
    try { await axios.delete(`${API}/connectors/${provider}`); loadConnStatus(); toast.success("Disconnected"); }
    catch (_) { toast.error("Could not disconnect"); }
  };

  return (
    <div className="space-y-5" data-testid="project-detail">
      {/* header */}
      <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-[var(--qn-foreground)]" data-testid="project-detail-name">{project.name}</h2>
              <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)] text-[var(--qn-foreground-muted)]">
                {SWARM_LABEL[project.swarm] || project.swarm}
              </span>
            </div>
            {project.description && <p className="mt-1 text-sm text-[var(--qn-foreground-muted)]">{project.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={archive} data-testid="project-archive-btn" title={project.archived ? "Unarchive" : "Archive"}
              className="p-2 rounded-lg border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]">
              {project.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            </button>
            <button onClick={del} data-testid="project-delete-btn" title="Delete"
              className="p-2 rounded-lg border border-[var(--qn-border)] text-red-400 hover:bg-red-500/10">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* insights */}
      {insights && (
        <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5" data-testid="project-insights">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[var(--qn-primary)]" />
            <span className="text-sm font-semibold text-[var(--qn-foreground)]">Insights</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <InsightStat icon={ListChecks} label="Runs" value={insights.runs.total} sub={`${insights.runs.completed} completed`} />
            <InsightStat icon={DollarSign} label="Value found" value={fmtMoney(insights.runs.total_value_found)} sub="across runs" accent />
            <InsightStat icon={BookOpen} label="Knowledge" value={insights.knowledge.ready} sub={`${insights.knowledge.sources} source(s)`} />
            <InsightStat icon={Layers} label="Chunks" value={insights.knowledge.total_chunks} sub="grounding" />
          </div>
          {insights.runs.top_runs.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--qn-foreground-muted)] font-semibold mb-2">Top runs by value</div>
              <div className="space-y-1.5" data-testid="project-insights-top-runs">
                {insights.runs.top_runs.filter((r) => r.value > 0).slice(0, 3).map((r) => (
                  <div key={r.task_id} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--qn-foreground)] truncate flex items-center gap-1.5">
                      {r.completed && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}{r.title}
                    </span>
                    <span className="font-mono text-[var(--qn-primary)] shrink-0">{fmtMoney(r.value)}</span>
                  </div>
                ))}
                {insights.runs.top_runs.every((r) => r.value === 0) && (
                  <div className="text-xs text-[var(--qn-foreground-muted)]">No valued runs yet — add completed swarm runs to see value.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* tasks */}
      <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-[var(--qn-primary)]" />
          <span className="text-sm font-semibold text-[var(--qn-foreground)]">Tasks in this project</span>
          <span className="text-xs text-[var(--qn-foreground-muted)]">({(project.tasks || []).length})</span>
        </div>
        {(project.tasks || []).length === 0 ? (
          <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="project-tasks-empty">
            No tasks yet. Use "Project" on a run in the swarm's task history to add one here.
          </p>
        ) : (
          <div className="space-y-2" data-testid="project-tasks-list">
            {project.tasks.map((t) => (
              <div key={t.id} data-testid={`project-task-${t.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--qn-foreground)] truncate">{t.task_title || t.run_id}</div>
                  <div className="text-[11px] text-[var(--qn-foreground-subtle)] font-mono truncate">{t.run_id}</div>
                </div>
                <button onClick={() => removeTask(t.id)} data-testid={`project-task-remove-${t.id}`}
                  className="p-1.5 rounded text-[var(--qn-foreground-subtle)] hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* knowledge */}
      <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[var(--qn-primary)]" />
            <span className="text-sm font-semibold text-[var(--qn-foreground)]">Always-used knowledge</span>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="project-knowledge-upload-btn"
            className="flex items-center gap-2 text-sm rounded-lg border border-[var(--qn-primary)]/50 text-[var(--qn-primary)] px-3 py-1.5 hover:bg-[var(--qn-primary-muted)] disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload
          </button>
          <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.csv,.html,.pptx" data-testid="project-knowledge-file-input"
            onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
        </div>

        {/* Connectors */}
        {connStatus && (
          <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="project-connectors">
            {[
              { key: "google", label: "Google Drive", st: connStatus.google },
              { key: "microsoft", label: "SharePoint", st: connStatus.microsoft },
            ].map(({ key, label, st }) => (
              <div key={key} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2" data-testid={`connector-${key}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <UploadCloud className="w-4 h-4 text-[var(--qn-primary)] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-[var(--qn-foreground)]">{label}</div>
                    <div className="text-[11px] text-[var(--qn-foreground-subtle)] truncate">
                      {!st.configured ? "Not configured" : st.connected ? (st.email || "Connected") : "Not connected"}
                    </div>
                  </div>
                </div>
                {st.connected ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setImportModal(key)} data-testid={`connector-import-${key}`}
                      className="text-xs rounded-lg bg-[var(--qn-primary)] text-white px-2.5 py-1.5">Import</button>
                    <button onClick={() => disconnect(key)} data-testid={`connector-disconnect-${key}`}
                      className="text-xs rounded-lg border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] px-2 py-1.5">Disconnect</button>
                  </div>
                ) : (
                  <button onClick={() => connect(key)} disabled={!st.configured} data-testid={`connector-connect-${key}`}
                    className="text-xs rounded-lg border border-[var(--qn-primary)]/50 text-[var(--qn-primary)] px-3 py-1.5 disabled:opacity-40 shrink-0">Connect</button>
                )}
              </div>
            ))}
          </div>
        )}
        {(project.knowledge_sources || []).length === 0 ? (
          <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="project-knowledge-empty">
            Upload documents (PDF, DOCX, TXT…). They're chunked, embedded and always retrieved as grounding for this swarm's agents.
          </p>
        ) : (
          <div className="space-y-2" data-testid="project-knowledge-list">
            {project.knowledge_sources.map((s) => {
              const chip = statusChip[s.status] || statusChip.processing;
              return (
                <div key={s.id} data-testid={`project-knowledge-${s.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[var(--qn-foreground-muted)] shrink-0" />
                    <span className="text-sm text-[var(--qn-foreground)] truncate">{s.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="flex items-center gap-1 text-[11px]" style={{ color: chip.color }}>
                      <chip.Icon className={`w-3 h-3 ${chip.label !== "Ready" && chip.label !== "Failed" ? "animate-spin" : ""}`} /> {chip.label}
                    </span>
                    <button onClick={() => deleteSource(s.id)} data-testid={`project-knowledge-remove-${s.id}`}
                      className="p-1 rounded text-[var(--qn-foreground-subtle)] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* grounding tester */}
        <div className="mt-4 pt-4 border-t border-[var(--qn-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[var(--qn-secondary)]" />
            <span className="text-xs uppercase tracking-wider text-[var(--qn-foreground-muted)] font-semibold">Test grounding retrieval</span>
          </div>
          <div className="flex gap-2">
            <input value={gq} onChange={(e) => setGq(e.target.value)} data-testid="project-grounding-query"
              onKeyDown={(e) => { if (e.key === "Enter") runGrounding(); }}
              placeholder="Ask what an agent would retrieve, e.g. award ceiling"
              className="flex-1 bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] outline-none" />
            <button onClick={runGrounding} disabled={gLoading || !gq.trim()} data-testid="project-grounding-run"
              className="flex items-center gap-2 text-sm rounded-lg bg-[var(--qn-secondary)] text-white px-4 py-2 disabled:opacity-50">
              {gLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Retrieve
            </button>
          </div>
          {grounding && (
            <div className="mt-3" data-testid="project-grounding-result">
              <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">
                {grounding.chunks_used} chunk(s) used · {grounding.chunks_considered} considered
              </div>
              {grounding.citations.length === 0 && (
                <p className="text-sm text-[var(--qn-foreground-muted)]">No grounding matched. Upload knowledge first.</p>
              )}
              {grounding.chunks.map((c, i) => (
                <div key={i} className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] p-3 mb-2">
                  <div className="flex items-center gap-2 text-[11px] text-[var(--qn-foreground-muted)] mb-1">
                    <span className="font-mono text-[var(--qn-secondary)]">[{i + 1}]</span>
                    <span className="truncate">{c.document_title}</span>
                    <span className="ml-auto px-1.5 py-0.5 rounded bg-[var(--qn-card)] border border-[var(--qn-border)]">{c.grounding_scope}</span>
                    <span className="font-mono">{(c.relevance_score * 100).toFixed(0)}%</span>
                  </div>
                  <p className="text-xs text-[var(--qn-foreground)] line-clamp-3">{c.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {importModal && (
        <ConnectorImportModal
          provider={importModal}
          projectId={project.id}
          onClose={() => setImportModal(null)}
          onImported={() => { onChanged(); }}
        />
      )}
    </div>
  );
}
